// useTableState.ts
import { useCallback, useMemo, useState } from 'react';
import type {
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import {
  Page,
  TestCalculationOutputDataParams,
  usePageSizeOptions,
} from '@atlas-frontend/sdk';
import { PaginationProps } from 'antd';

export function useOutputDataPagination(page?: Page<Record<string, any>>) {
  const [pageData, setPageData] = useState(page);
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const pageSizeOptions = usePageSizeOptions();

  const handleTableFilter: OnChangeFn<ColumnFiltersState> = useCallback(
    (updaterOrValue) => {
      setColumnFilters((current) =>
        updaterOrValue instanceof Function
          ? updaterOrValue(current)
          : updaterOrValue,
      );
    },
    [],
  );

  // These are calculated from the initial page
  const queryParameters: Partial<TestCalculationOutputDataParams> =
    useMemo(() => {
      const params: Partial<TestCalculationOutputDataParams> = {
        page: pageData?.number ?? 0,
        size: pageData?.size ?? 10,
        sort: sortingState
          .map((sort) => `${sort.id},${sort.desc ? 'desc' : 'asc'}`)
          .join(','),
        filterParams: columnFilters.reduce(
          (acc, filter) => {
            if (typeof filter.value !== 'undefined' && filter.value !== null) {
              acc[filter.id] = filter.value;
            }
            return acc;
          },
          {} as Record<string, any>,
        ),
      };

      return params;
    }, [columnFilters, pageData?.number, pageData?.size, sortingState]);

  const tableProps = useMemo(
    () => ({
      filters: columnFilters,
      onFilter: handleTableFilter,
      sort: sortingState,
      onSort: setSortingState,
    }),
    [columnFilters, handleTableFilter, sortingState],
  );

  const paginationProps: PaginationProps = useMemo(
    () => ({
      current: (pageData?.number ?? 0) + 1,
      pageSize: pageData?.size ?? 10,
      total: pageData?.totalElements || 0,
      onChange: (pageNumber, pageSize) => {
        setPageData((current) => {
          if (!current) return current;
          return {
            ...current,
            number: pageNumber - 1,
            size: pageSize,
          };
        });
      },
      showSizeChanger: true,
      pageSizeOptions,
    }),
    [
      pageData?.number,
      pageData?.size,
      pageData?.totalElements,
      pageSizeOptions,
    ],
  );

  return {
    queryParameters,
    tableProps,
    paginationProps,
    updatePageData: setPageData,
  };
}
